<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import {
  getVoiceCallProvider,
  VOICE_CALL_PROVIDERS,
} from 'dashboard/helper/inbox';
import {
  VOICE_CALL_DIRECTION,
  VOICE_CALL_OUTBOUND_INIT_STATUS,
} from 'dashboard/components-next/message/constants';
import { useWhatsappCallSession } from 'dashboard/composables/useWhatsappCallSession';
import { useCallsStore } from 'dashboard/stores/calls';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import WhatsappCallsAPI from 'dashboard/api/channel/whatsapp/whatsappCallsAPI';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
  chat: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();
const callsStore = useCallsStore();
const whatsappCallSession = useWhatsappCallSession();
const contactsUiFlags = useMapGetter('contacts/getUIFlags');
const { isCloudFeatureEnabled } = useAccount();
const isRequestingPermission = ref(false);

const voiceCallProvider = computed(() => getVoiceCallProvider(props.inbox));
const isVoiceCallInbox = computed(
  () =>
    voiceCallProvider.value !== null &&
    isCloudFeatureEnabled(FEATURE_FLAGS.CHANNEL_VOICE)
);
const isWhatsappVoiceInbox = computed(
  () => voiceCallProvider.value === VOICE_CALL_PROVIDERS.WHATSAPP
);
const hasWhatsappCallPermission = computed(
  () => !!props.chat?.additional_attributes?.call_permission_granted_at
);
const isWhatsappCallingRestricted = computed(
  () =>
    isWhatsappVoiceInbox.value &&
    !!props.inbox?.business_initiated_calling_restricted
);

const isCallButtonDisabled = computed(() => {
  if (callsStore.hasActiveCall || callsStore.hasIncomingCall) return true;
  if (isWhatsappVoiceInbox.value) {
    if (isWhatsappCallingRestricted.value) return true;
    return (
      whatsappCallSession.isInitiating.value || isRequestingPermission.value
    );
  }
  return contactsUiFlags.value?.isInitiatingCall || false;
});

const isCallButtonLoading = computed(() =>
  isWhatsappVoiceInbox.value
    ? whatsappCallSession.isInitiating.value || isRequestingPermission.value
    : !!contactsUiFlags.value?.isInitiatingCall
);

const callButtonTooltip = computed(() => {
  if (!isWhatsappVoiceInbox.value) {
    return t('CONVERSATION.HEADER.VOICE_CALL');
  }
  if (isWhatsappCallingRestricted.value) {
    return t('CONVERSATION.HEADER.WHATSAPP_CALL_RESTRICTED');
  }
  if (hasWhatsappCallPermission.value) {
    return t('CONVERSATION.HEADER.WHATSAPP_CALL');
  }
  return t('CONVERSATION.HEADER.WHATSAPP_CALL_PERMISSION_REQUEST');
});

const callButtonVariant = computed(() =>
  isWhatsappCallingRestricted.value || hasWhatsappCallPermission.value
    ? 'solid'
    : 'ghost'
);

const callButtonColor = computed(() => {
  if (isWhatsappCallingRestricted.value) return 'ruby';
  if (hasWhatsappCallPermission.value) return 'teal';
  return 'slate';
});

const requestPermission = async () => {
  if (isCallButtonDisabled.value) return;

  isRequestingPermission.value = true;
  try {
    const response = await WhatsappCallsAPI.requestPermission({
      conversationId: props.chat.id,
    });
    const message =
      response?.status === VOICE_CALL_OUTBOUND_INIT_STATUS.PERMISSION_PENDING
        ? t('CONVERSATION.HEADER.WHATSAPP_CALL_PERMISSION_PENDING')
        : t('CONVERSATION.HEADER.WHATSAPP_CALL_PERMISSION_REQUESTED');
    useAlert(message);
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      t('CONVERSATION.HEADER.WHATSAPP_CALL_PERMISSION_REQUEST_FAILED');
    useAlert(message);
  } finally {
    isRequestingPermission.value = false;
  }
};

const startWhatsappCall = async () => {
  if (whatsappCallSession.isInitiating.value) return;
  try {
    const response = await whatsappCallSession.initiateOutboundCall({
      conversationId: props.chat.id,
    });

    // Composable returns LOCKED when init is already in flight or a call is
    // active; soft no-op so a parallel click doesn't trigger a banner.
    if (response?.status === VOICE_CALL_OUTBOUND_INIT_STATUS.LOCKED) return;
    // Permission template path returns no call id — show banner, no widget yet.
    if (!response?.id) {
      const status = response?.status;
      const message =
        status === VOICE_CALL_OUTBOUND_INIT_STATUS.PERMISSION_PENDING
          ? t('CONVERSATION.HEADER.WHATSAPP_CALL_PERMISSION_PENDING')
          : t('CONVERSATION.HEADER.WHATSAPP_CALL_PERMISSION_REQUESTED');
      useAlert(message);
      return;
    }

    // Stay non-active until Meta delivers the connect webhook (sdp_answer);
    // flipping to active here would start the duration timer before pickup.
    callsStore.addCall({
      callSid: response.call_id,
      callId: response.id,
      conversationId: props.chat.id,
      inboxId: props.inbox?.id,
      callDirection: VOICE_CALL_DIRECTION.OUTBOUND,
      provider: VOICE_CALL_PROVIDERS.WHATSAPP,
    });
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      error?.message ||
      t('CONVERSATION.HEADER.WHATSAPP_CALL_FAILED');
    useAlert(message);
  }
};

const startTwilioCall = async () => {
  if (contactsUiFlags.value?.isInitiatingCall) return;
  try {
    const response = await store.dispatch('contacts/initiateCall', {
      contactId: props.chat?.meta?.sender?.id,
      inboxId: props.inbox?.id,
      conversationId: props.chat.id,
    });

    callsStore.addCall({
      callSid: response?.call_sid,
      conversationId: response?.conversation_id ?? props.chat.id,
      inboxId: props.inbox?.id,
      callDirection: VOICE_CALL_DIRECTION.OUTBOUND,
    });
  } catch (error) {
    useAlert(error?.message || t('CONVERSATION.HEADER.VOICE_CALL_FAILED'));
  }
};

const startCall = () => {
  if (isWhatsappVoiceInbox.value) {
    if (isWhatsappCallingRestricted.value) return undefined;
    return hasWhatsappCallPermission.value
      ? startWhatsappCall()
      : requestPermission();
  }
  return startTwilioCall();
};
</script>

<template>
  <NextButton
    v-if="isVoiceCallInbox"
    v-tooltip.bottom="callButtonTooltip"
    sm
    :variant="callButtonVariant"
    :color="callButtonColor"
    icon="i-lucide-phone"
    :is-loading="isCallButtonLoading"
    :disabled="isCallButtonDisabled"
    @click="startCall"
  />
  <template v-else />
</template>
